import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from './components/ErrorBoundary';
import Notifications from './components/Notifications';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { syncFCMToken, setupForegroundNotifications, startTokenHeartbeat } from './utils/fcm';
import './index.css';

// Lazy load all pages (this dramatically reduces initial bundle size)
const Home = lazy(() => import('./pages/Home'));
const Profile = lazy(() => import('./pages/Profile'));
const Contact = lazy(() => import('./pages/Contact'));
const ProviderProfile = lazy(() => import('./pages/ProviderProfile'));
const ManageOrders = lazy(() => import('./pages/ManageOrders'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CategoriesList = lazy(() => import('./pages/CategoriesList'));
const CategoryHub = lazy(() => import('./pages/CategoryHub'));
const CategoryLocation = lazy(() => import('./pages/CategoryLocation'));
const Blog = lazy(() => import('./pages/Blog'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Removed SearchProviders completely as requested

const queryClient = new QueryClient();

function AppContent({ chatSocket, setChatSocket }) {
  const navigate = useNavigate();
  const location = useLocation();

  // FCM Setup
  useEffect(() => {
    let stopHeartbeat;
    let unsubscribe;

    const setupFCM = async () => {
      try {
        await syncFCMToken();
        stopHeartbeat = await startTokenHeartbeat();
        unsubscribe = setupForegroundNotifications(navigate);
      } catch (err) {
        console.error('[App] Error in FCM setup:', err);
      }
    };

    setupFCM();

    return () => {
      if (unsubscribe) unsubscribe();
      if (stopHeartbeat) stopHeartbeat();
    };
  }, [navigate]);

  // Google Analytics tracking
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-HDE3BPR4FP', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <div className="App">
        <Toaster position="top-center" richColors closeButton />
        <Notifications userId={undefined} />

        {/* Suspense wrapper for lazy-loaded routes */}
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
            <div className="text-lg text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* /search route completely removed */}
            
            <Route path="/provider/:providerSlug" element={<ProviderProfile />} />
            <Route path="/manage-orders" element={<ManageOrders />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/categories" element={<CategoriesList />} />
            <Route path="/categories/:slug" element={<CategoryHub />} />
            <Route path="/categories/:slug/:location" element={<CategoryLocation />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<ArticlePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  const [chatSocket, setChatSocket] = useState(null);

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename="/">
        <AuthProvider>
          <ThemeProvider>
            <AppContent
              chatSocket={chatSocket}
              setChatSocket={setChatSocket}
            />
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
